import React, { useRef } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { PaymentForm } from '../PaymentForm'
import { societyQueries, startPremium } from '../../../../api/societies'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'

interface PaymentModalProps {
  show: boolean
  close: () => void
  society: ISocietiesModel
  discountCode?: string
}

export const PaymentModal = ({
  society,
  show,
  close,
  discountCode,
}: PaymentModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const ref = useRef(null)
  const { data: premiumInformation, isLoading: isFetchingPremiumInformation } =
    useQuery(
      societyQueries.getSocietyPremiumInformation(society._id, discountCode)
    )

  const {
    mutate,
    isPending: isUpgrading,
    isError: upgradeError,
  } = useMutation({
    mutationFn: async () => {
      await startPremium(society._id, discountCode)
    },
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('invoiceView.flashMessage.upgradeSuccess')
      )
      close()
    },
  })

  if (isFetchingPremiumInformation || !premiumInformation) {
    return null
  }

  const onSubmit = async (): Promise<void> => {
    mutate()
  }

  return (
    <Modal ref={ref} show={show} size="xl">
      {{
        header: (
          <ModalHeader onClose={close}>
            {translate('invoiceView.modals.payment.title')}
          </ModalHeader>
        ),
        body: (
          <ModalBody className="overflow-y-auto">
            <PaymentForm
              society={society}
              onSubmit={onSubmit}
              onClose={close}
              loading={isUpgrading}
              costSummary={{
                baseCost: premiumInformation.baseCost,
                cost: premiumInformation.cost,
                discountAmount: premiumInformation.discountAmount,
                partner: premiumInformation.partner,
              }}
              upgradeErrored={upgradeError}
            />
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
